import { AppNav, type AppNavItem, type AppNavRow } from '@mdvip/shared/features/app-nav/AppNav';
import { initials } from '@mdvip/shared/lib/initials';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { useAbility } from '@mdvip/shared/stores/authStore';
import type { Subject } from '@mdvip/shared/types/ability';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReferAFriendModal } from '@/components/refer-a-friend/ReferAFriendModal';
import { useAuth } from '@/contexts/AuthContext';

/** CASL subject the member must be able to read for the entry to render at all. */
type Gated = { requires?: Subject };
type ItemSpec = Omit<AppNavItem, 'label' | 'shortLabel'> & Gated & { labelKey: string };
type RowSpec = Omit<AppNavRow, 'label'> & Gated & { labelKey: string };

/* `as const satisfies` rather than an annotation: the literal `labelKey` types are what let t()
   key-check the labels, and the annotation would widen them. */
const NAV_ITEMS = [
  { to: '/', icon: 'gauge', labelKey: 'nav.dashboard' },
  { to: '/payments', icon: 'creditCard', labelKey: 'payments.nav', requires: 'Payment' },
  { to: '/resources', icon: 'bookOpen', labelKey: 'nav.resources' },
  { to: '/bookmarks', icon: 'bookmark', labelKey: 'nav.bookmarks' },
] as const satisfies readonly ItemSpec[];

const ACCOUNT_ROWS = [
  { key: 'profile', labelKey: 'nav.profileMembership', icon: 'user', to: '/profile', testId: 'account-profile-item' },
  {
    key: 'payments',
    labelKey: 'nav.managePayments',
    icon: 'creditCard',
    to: '/payments',
    testId: 'account-payments-item',
    requires: 'Payment',
  },
] as const satisfies readonly RowSpec[];

/* `refer`'s `onSelect` needs `openReferModal`, which only exists once rendering starts, so this
   list is built inside `Navigation()` rather than at module scope like `ACCOUNT_ROWS`. */
const buildMoreRows = (openReferModal: () => void) =>
  [
    { key: 'notifications', labelKey: 'nav.notifications' },
    { key: 'bookmarks', labelKey: 'nav.myBookmarks', to: '/bookmarks', testId: 'account-bookmarks-item' },
    { key: 'refer', labelKey: 'nav.referFriend', onSelect: openReferModal },
    { key: 'help', labelKey: 'nav.helpSupport', to: '/contact' },
  ] as const satisfies readonly RowSpec[];

/* The label keys are derived back out of the data rather than re-listed, so t() still
   key-checks them: the specs' own `labelKey: string` would widen them. */
type LabelKey =
  | (typeof NAV_ITEMS)[number]['labelKey']
  | (typeof ACCOUNT_ROWS)[number]['labelKey']
  | ReturnType<typeof buildMoreRows>[number]['labelKey'];

export function Navigation() {
  const { user, logout } = useAuth();
  const ability = useAbility();
  const { t } = useTranslation('members');
  const [referModalOpen, setReferModalOpen] = useState(false);
  // Bumped only on open (never on close) so `<ReferAFriendModal key={referModalSession}>` remounts
  // with fresh state on the *next* open, without remounting mid-close-animation. The modal persists
  // its own state across open/close by design (matches OneTimePaymentModal's `paymentSession`), so
  // starting fresh is the caller's job, not the modal's.
  const [referModalSession, setReferModalSession] = useState(0);
  const openReferModal = () => {
    setReferModalSession((session) => session + 1);
    setReferModalOpen(true);
  };

  // A null ability (permissions not fetched, or the fetch failed) denies, so a gated entry stays
  // hidden rather than flashing in before the rules land. `labelKey` is in the signature only to
  // keep this off TypeScript's weak-type path: a parameter whose every property is optional
  // rejects an argument that shares none of them, which is exactly the ungated entries.
  const allowed = (entry: { labelKey: string; requires?: Subject }): boolean => {
    const subject = entry.requires;
    return !subject || (ability?.can('read', subject) ?? false);
  };

  const resolveRow = ({ labelKey, requires: _requires, ...row }: RowSpec & { labelKey: LabelKey }): AppNavRow => ({
    ...row,
    label: t(labelKey),
  });
  const resolveItem = ({ labelKey, requires: _requires, ...item }: ItemSpec & { labelKey: LabelKey }): AppNavItem => ({
    ...item,
    label: t(labelKey),
  });

  const memberServices = MDVIP_COMPANY_INFO.memberSupportPhone;

  return (
    <>
      <AppNav
        items={NAV_ITEMS.filter(allowed).map(resolveItem)}
        account={{
          initials: initials({ firstName: user?.firstName, lastName: user?.lastName }),
          name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
          secondaryLine: user?.memberId ? t('nav.memberId', { id: user.memberId }) : undefined,
          rows: ACCOUNT_ROWS.filter(allowed).map(resolveRow),
          moreRows: buildMoreRows(openReferModal).map(resolveRow),
          support: {
            phone: memberServices,
            callLabel: t('nav.callMemberServices', { number: memberServices }),
          },
          onLogout: () => void logout(),
        }}
        labels={{
          primary: t('nav.primary'),
          bottomBar: t('nav.bottomBar'),
          accountMenu: t('nav.accountMenu'),
          myAccount: t('nav.myAccount'),
          more: t('nav.more'),
          closeMenu: t('nav.closeMenu'),
          logout: t('nav.logout'),
        }}
      />
      <ReferAFriendModal key={referModalSession} open={referModalOpen} onOpenChange={setReferModalOpen} />
    </>
  );
}
